import { Check, Copy, Loader2, Mail, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { type ParametriMail, testoInviato, useCpAvvisoPreparato, useTestoAvviso } from "@/hooks/useM3";
import { copia } from "@/lib/appunti";
import { cn } from "@/lib/utils";
import type { DestinatarioAvviso, LinguaAvviso, TestoAvviso } from "@/types/api";

const LINGUE: { valore: LinguaAvviso; etichetta: string }[] = [
  { valore: "it", etichetta: "Italiano" },
  { valore: "en", etichetta: "English" },
  { valore: "fr", etichetta: "Français" },
  { valore: "es", etichetta: "Español" },
];

/** Il testo come l'ha lasciato Marina, quando preme l'azione principale. */
export interface MailScritta {
  oggetto: string;
  corpo: string;
}

/**
 * L'editor della mail di avviso, uguale nel «+ Nuovo carico» e nel drawer
 * (01-10-2026, secondo giro).
 *
 * Il testo proposto dal portale è **modificabile**: Marina può aggiungere quello
 * che il portale non sa («il camion deve arrivare entro le 14»). Le righe che
 * aggiunge finiscono nelle Note del carico, datate — lo fa il server quando
 * riceve il testo, confrontandolo col proposto.
 *
 * Cambiare lingua, destinatario o data rigenera il testo: se Marina l'ha già
 * modificato, prima si chiede, o perderebbe le sue aggiunte senza saperlo.
 *
 * La data prevista dell'ordine viene dall'export e spesso non è reale (Enrico,
 * 16-08): sta in un campo che si corregge o si toglie.
 */
export function EditorMail({
  testo,
  caricamento,
  errore,
  parametri,
  onParametri,
  haSpedizioniere,
  azione,
}: {
  testo: TestoAvviso | undefined;
  caricamento: boolean;
  errore?: string | null;
  parametri: ParametriMail;
  onParametri: (p: ParametriMail) => void;
  haSpedizioniere?: boolean;
  azione: {
    etichetta: string;
    inCorso?: boolean;
    fatta?: boolean;
    onClick: (m: MailScritta) => void;
  };
}) {
  const [oggetto, setOggetto] = useState("");
  const [corpo, setCorpo] = useState("");
  const [modificato, setModificato] = useState(false);
  const [copiato, setCopiato] = useState<string | null>(null);
  const [erroreCopia, setErroreCopia] = useState<string | null>(null);
  const ultimo = useRef<TestoAvviso | undefined>(undefined);

  // Un testo nuovo dal server sostituisce quello a schermo solo se Marina non
  // l'ha toccato (o ha accettato di perderlo: vedi `cambia`).
  useEffect(() => {
    if (!testo || testo === ultimo.current) return;
    ultimo.current = testo;
    if (!modificato) {
      setOggetto(testo.oggetto);
      setCorpo(testo.corpo);
    }
  }, [testo, modificato]);

  function cambia(p: Partial<ParametriMail>) {
    if (
      modificato &&
      !window.confirm("Hai modificato il testo: rigenerandolo perdi le modifiche. Continuo?")
    ) {
      return;
    }
    setModificato(false);
    onParametri({ ...parametri, ...p });
  }

  async function copiaCampo(chi: string, valore: string) {
    setErroreCopia(null);
    if (!(await copia(valore))) {
      setErroreCopia("Il browser non ha permesso di copiare: seleziona il testo e copialo a mano.");
      return;
    }
    setCopiato(chi);
    setTimeout(() => setCopiato((c) => (c === chi ? null : c)), 1800);
  }

  const dataMostrata = parametri.dataOrdine ?? testo?.data_ordine ?? "";

  return (
    <div className="space-y-3" data-testid="testo-avviso">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Lingua
          <select
            aria-label="Lingua della mail"
            value={parametri.lingua}
            onChange={(e) => cambia({ lingua: e.target.value as LinguaAvviso })}
            className="h-8 rounded-md border bg-card px-2 text-xs text-foreground"
          >
            {LINGUE.map((l) => (
              <option key={l.valore} value={l.valore}>
                {l.etichetta}
              </option>
            ))}
          </select>
        </label>
        <div className="inline-flex rounded-md border p-0.5 text-xs" role="group" aria-label="Per chi">
          {(["cliente", "spedizioniere"] as DestinatarioAvviso[]).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => parametri.destinatario !== d && cambia({ destinatario: d })}
              className={cn(
                "rounded px-2.5 py-1",
                parametri.destinatario === d ? "bg-brand-700 font-semibold text-white" : "hover:bg-muted",
              )}
            >
              {d === "cliente" ? "Per il cliente" : "Per lo spedizioniere"}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Data prevista dell'ordine
          <input
            type="date"
            aria-label="Data prevista dell'ordine"
            value={dataMostrata}
            onChange={(e) => cambia({ dataOrdine: e.target.value })}
            className="h-8 rounded-md border bg-card px-2 text-xs text-foreground"
          />
          {dataMostrata && (
            <button
              type="button"
              title="Togli la data dalla mail"
              aria-label="Togli la data dalla mail"
              onClick={() => cambia({ dataOrdine: "" })}
              className="rounded p-0.5 hover:text-red-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </label>
      </div>
      {parametri.destinatario === "spedizioniere" && !haSpedizioniere && (
        <p className="text-[11px] text-amber-700">Sul carico non c'è ancora lo spedizioniere.</p>
      )}

      {caricamento && !testo ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparo il testo…
        </p>
      ) : errore ? (
        <p className="text-xs text-red-700">Il testo non è arrivato: {errore}</p>
      ) : testo ? (
        <>
          <Campo
            etichetta="A"
            valore={testo.a.join(", ")}
            vuoto={
              parametri.destinatario === "cliente"
                ? "Nessun indirizzo certo per questo cliente: mettilo tu."
                : "Nessun indirizzo dello spedizioniere in anagrafica: mettilo tu."
            }
            copiato={copiato === "a"}
            onCopia={() => copiaCampo("a", testo.a.join("; "))}
          />
          <Campo
            etichetta="Cc"
            valore={testo.cc.join(", ")}
            nota="Aida in copia: è così che vede la mail e aggiorna questo carico."
            copiato={copiato === "cc"}
            onCopia={() => copiaCampo("cc", testo.cc.join("; "))}
          />
          <div className="flex items-start gap-2 text-xs">
            <span className="w-14 shrink-0 pt-1.5 text-muted-foreground">Oggetto</span>
            <input
              aria-label="Oggetto della mail"
              value={oggetto}
              onChange={(e) => {
                setOggetto(e.target.value);
                setModificato(true);
              }}
              className="h-8 min-w-0 flex-1 rounded border bg-card px-2"
            />
            <button
              type="button"
              onClick={() => copiaCampo("oggetto", oggetto)}
              title="Copia oggetto"
              aria-label="Copia oggetto"
              className="mt-0.5 rounded border p-1.5 hover:bg-muted"
            >
              {copiato === "oggetto" ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                Testo{" "}
                {modificato && <span className="font-normal text-amber-700">· modificato da te</span>}
              </span>
            </div>
            <textarea
              aria-label="Testo della mail"
              value={corpo}
              onChange={(e) => {
                setCorpo(e.target.value);
                setModificato(true);
              }}
              rows={Math.min(20, Math.max(10, corpo.split("\n").length + 1))}
              className="w-full resize-y rounded-md border bg-card p-2.5 font-mono text-[11.5px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="mt-1 flex items-start gap-1.5 text-[11px] text-muted-foreground">
              <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Puoi scriverci quello che vuoi: le righe che aggiungi finiscono anche nelle Note del
              carico, con la data. Se aggiungi una commessa, Aida segnerà il carico da verificare.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2">
            {azione.fatta && (
              <span className="flex items-center gap-1 text-xs font-medium text-emerald-700" role="status">
                <Check className="h-3.5 w-3.5" /> Testo copiato
              </span>
            )}
            <button
              type="button"
              disabled={azione.inCorso || !corpo.trim()}
              onClick={() => azione.onClick({ oggetto, corpo })}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {azione.inCorso ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
              {azione.etichetta}
            </button>
          </div>
        </>
      ) : null}
      {erroreCopia && <p className="text-xs text-red-700">{erroreCopia}</p>}
    </div>
  );
}

/**
 * La mail di una riga che esiste già (dal drawer): lo stesso editor, e
 * «Copia il testo» segna la riga come «mail preparata» e mette le aggiunte di
 * Marina nelle Note.
 */
export function TestoAvvisoPannello({
  caricoId,
  haSpedizioniere,
}: {
  caricoId: string;
  haSpedizioniere?: boolean;
}) {
  const [parametri, setParametri] = useState<ParametriMail>({ lingua: "it", destinatario: "cliente" });
  const { data, isLoading, error } = useTestoAvviso(caricoId, parametri);
  const preparato = useCpAvvisoPreparato(caricoId);
  const [errore, setErrore] = useState<string | null>(null);

  return (
    <>
      <EditorMail
        testo={data}
        caricamento={isLoading}
        errore={error ? (error as Error).message : null}
        parametri={parametri}
        onParametri={setParametri}
        haSpedizioniere={haSpedizioniere}
        azione={{
          etichetta: "Copia il testo",
          inCorso: preparato.isPending,
          fatta: preparato.isSuccess,
          onClick: async (m) => {
            setErrore(null);
            if (!(await copia(m.corpo))) {
              setErrore("Il browser non ha permesso di copiare: seleziona il testo e copialo a mano.");
              return;
            }
            preparato.mutate(testoInviato(m.corpo, parametri), {
              onError: (e) => setErrore((e as Error).message),
            });
          },
        }}
      />
      {preparato.isSuccess && (
        <p className="text-[11px] font-medium text-emerald-700">
          Sul carico è segnato «mail preparata»: quando Aida vedrà la mail, lo aggiornerà.
        </p>
      )}
      {errore && <p className="text-xs text-red-700">{errore}</p>}
    </>
  );
}

function Campo({
  etichetta,
  valore,
  vuoto,
  nota,
  copiato,
  onCopia,
}: {
  etichetta: string;
  valore: string;
  vuoto?: string;
  nota?: string;
  copiato: boolean;
  onCopia: () => void;
}) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="w-14 shrink-0 pt-1.5 text-muted-foreground">{etichetta}</span>
      <span className="min-w-0 flex-1">
        <span className={cn("block rounded border bg-muted/30 px-2 py-1.5", !valore && "text-muted-foreground")}>
          {valore || vuoto || "—"}
        </span>
        {nota && <span className="mt-0.5 block text-[10.5px] text-muted-foreground">{nota}</span>}
      </span>
      {valore && (
        <button
          type="button"
          onClick={onCopia}
          title={`Copia ${etichetta.toLowerCase()}`}
          aria-label={`Copia ${etichetta.toLowerCase()}`}
          className="mt-0.5 rounded border p-1.5 hover:bg-muted"
        >
          {copiato ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      )}
    </div>
  );
}
